import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);
    this.querySelectorAll('iframe:not([title])').forEach(this.#nameFrame);
    this.querySelectorAll('img').forEach(this.#watchImage);
  }

  /**
   * Hide an image in page or blog content whose file is gone (deleted from
   * Shopify Files, a dead external URL) instead of showing a broken-image
   * box. If the image was all a link or figure held, that goes too, so no
   * empty clickable area is left behind.
   * @param {HTMLImageElement} img
   */
  #watchImage = (img) => {
    const hide = () => {
      img.hidden = true;
      const wrapper = img.closest('a, figure');
      if (wrapper instanceof HTMLElement && this.contains(wrapper) && !wrapper.textContent?.trim() && !wrapper.querySelector('img:not([hidden])')) {
        wrapper.hidden = true;
      }
    };
    if (img.complete) {
      if (img.naturalWidth === 0 && img.currentSrc) hide();
    } else {
      img.addEventListener('error', hide, { once: true });
    }
  };

  /**
   * Give an embed pasted into page or blog content an accessible name. Frames
   * need one (WCAG 4.1.2), and the embed codes YouTube, Vimeo and Google Maps
   * hand out don't always include a title -- several store pages shipped
   * without. A title written in the content itself is left alone.
   * @param {HTMLIFrameElement} frame
   */
  #nameFrame(frame) {
    const src = frame.getAttribute('src') ?? '';
    let title = 'Embedded content';
    if (/youtube(-nocookie)?\.com|youtu\.be|vimeo\.com/.test(src)) title = 'Video';
    else if (/google\.[a-z.]+\/maps|maps\.google\./.test(src)) title = 'Map';
    frame.setAttribute('title', title);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
